import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Target, Clock } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ModuleType } from '@/lib/moduleContext';
import { getDailyGoalDefaults } from '@/lib/moduleDailyGoals';

interface HeaderProgressIndicatorProps {
  moduleType: ModuleType;
}

export function HeaderProgressIndicator({ moduleType }: HeaderProgressIndicatorProps) {
  const { user } = useAuth();
  const today = new Date().toISOString().split('T')[0];

  // Fetch user daily goals for this module
  const { data: goals } = useQuery({
    queryKey: ['daily-goals-header', user?.id, moduleType],
    queryFn: async () => {
      if (!user) return null;
      const { data } = await supabase
        .from('user_daily_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('module_type', moduleType);
      return data;
    },
    enabled: !!user,
    staleTime: 30000,
    refetchOnMount: 'always',
  });

  // Fetch today's progress, scoped to the current module
  const { data: progress } = useQuery({
    queryKey: ['daily-progress-header', user?.id, today, moduleType],
    queryFn: async () => {
      if (!user) return { questions: 0, time: 0 };

      const { data: answers } = await supabase
        .from('user_answers')
        .select('id')
        .eq('is_official', true)
        .eq('user_id', user.id)
        .eq('module_type', moduleType)
        .gte('answered_at', `${today}T00:00:00`)
        .lte('answered_at', `${today}T23:59:59`);

      const questionsCount = answers?.length || 0;

      const { data: sessions } = await supabase
        .from('pomodoro_sessions')
        .select('total_minutes')
        .eq('user_id', user.id)
        .eq('module_type', moduleType)
        .eq('session_date', today);

      const totalMinutes = sessions?.reduce((acc, s) => acc + (s.total_minutes || 0), 0) || 0;

      return { questions: questionsCount, time: totalMinutes };
    },
    enabled: !!user,
    refetchInterval: 60000,
  });

  const defaults = getDailyGoalDefaults(moduleType);
  const questionsGoal = goals?.find(g => g.goal_type === 'questions')?.target_value || defaults.questions;
  const timeGoal = goals?.find(g => g.goal_type === 'study_time')?.target_value || defaults.time;

  const questionsProgress = Math.min(100, ((progress?.questions || 0) / questionsGoal) * 100);
  const timeProgress = Math.min(100, ((progress?.time || 0) / timeGoal) * 100);

  const questionsComplete = (progress?.questions || 0) >= questionsGoal;
  const timeComplete = (progress?.time || 0) >= timeGoal;

  return (
    <div className="flex items-center gap-3">
      {/* Questions Progress */}
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 cursor-default">
            <Target className={cn(
              "h-3.5 w-3.5 transition-colors",
              questionsComplete ? "text-emerald-500" : "text-muted-foreground"
            )} />
            <span className={cn(
              "text-xs font-medium tabular-nums",
              questionsComplete && "text-emerald-500"
            )}>
              {progress?.questions || 0}/{questionsGoal}
            </span>
            <Progress 
              value={questionsProgress} 
              className={cn(
                "w-8 h-1.5",
                questionsComplete && "[&>div]:bg-emerald-500"
              )}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="font-medium">Questões hoje</p>
          <p className="text-xs text-muted-foreground">
            {progress?.questions || 0} de {questionsGoal} questões
          </p>
        </TooltipContent>
      </Tooltip>

      {/* Time Progress */}
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1.5 cursor-default">
            <Clock className={cn(
              "h-3.5 w-3.5 transition-colors",
              timeComplete ? "text-emerald-500" : "text-muted-foreground"
            )} />
            <span className={cn(
              "text-xs font-medium tabular-nums",
              timeComplete && "text-emerald-500"
            )}>
              {progress?.time || 0}/{timeGoal}
            </span>
            <Progress 
              value={timeProgress} 
              className={cn(
                "w-8 h-1.5",
                timeComplete && "[&>div]:bg-emerald-500"
              )}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          <p className="font-medium">Tempo de estudo</p>
          <p className="text-xs text-muted-foreground">
            {progress?.time || 0} de {timeGoal} minutos
          </p>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}
